/**
 * useTextSelection Hook
 * 
 * Custom hook for handling text selection and share functionality.
 * Provides selection detection, context menu management, and markdown detection.
 */

import { useState, useEffect, useCallback, useRef } from 'react';

// Utility to detect if text contains markdown formatting
function hasMarkdownFormatting(text) {
  if (!text) return false;
  
  // Common markdown patterns
  const markdownPatterns = [
    /\*\*.*?\*\*/g, // Bold **text**
    /\*.*?\*/g, // Italic *text*
    /`.*?`/g, // Inline code `text`
    /^#{1,6}\s/gm, // Headers # ## ###
    /^[-*]\s/gm, // Bullet lists - item or * item
    /^•\s/gm, // Bullet lists with dot char from rendered summary
    /^\d+\.\s/gm, // Numbered lists 1. item
    /\[.*?\]\(.*?\)/g, // Links [text](url)
    /^>\s/gm, // Blockquotes > text
  ];
  
  return markdownPatterns.some(pattern => pattern.test(text));
}

// Utility to extract markdown from selected content
function extractMarkdownFromSelection(selection) {
  const selectedText = selection.toString().trim();
  const range = selection.getRangeAt(0);
  const container = range.commonAncestorContainer;
  
  // Try to find the closest element that might contain markdown source
  let markdownContainer = container.nodeType === Node.TEXT_NODE 
    ? container.parentElement 
    : container;
    
  // Look for data attributes or markdown source (walk up a few ancestors)
  while (markdownContainer && markdownContainer !== document.body) {
    if (markdownContainer.dataset && markdownContainer.dataset.markdown) {
      const fullMarkdown = markdownContainer.dataset.markdown;
      return extractRelevantMarkdown(fullMarkdown, selectedText);
    }
    if (markdownContainer.getAttribute && markdownContainer.getAttribute('data-markdown-source')) {
      const fullMarkdown = markdownContainer.getAttribute('data-markdown-source');
      return extractRelevantMarkdown(fullMarkdown, selectedText);
    }
    // Support elements that mirror markdown in a data-md attribute
    if (markdownContainer.getAttribute && markdownContainer.getAttribute('data-md')) {
      const fullMarkdown = markdownContainer.getAttribute('data-md');
      return extractRelevantMarkdown(fullMarkdown, selectedText);
    }
    markdownContainer = markdownContainer.parentElement;
  }
  
  // Fallback: if no markdown source found, try to pull markdown from known article containers
  try {
    const el = markdownContainer?.closest('[data-markdown-source], [data-markdown]');
    if (el) {
      const source = el.getAttribute('data-markdown-source') || el.dataset.markdown;
      if (source) {
        const md = extractRelevantMarkdown(source, selectedText);
        if (md && md.trim().length > 0) return md;
      }
    }
  } catch (_) {
    // no-op
  }

  // Last resort: reconstruct minimal markdown from the actual HTML selection
  try {
    const fragment = range.cloneContents();
    const markdown = convertHtmlFragmentToMarkdown(fragment).trim();
    if (markdown && markdown.length > 0) {
      return markdown;
    }
  } catch (_) {
    // ignore
  }
  return selectedText;
}

// Helper function to extract relevant markdown portion based on selected text
function extractRelevantMarkdown(fullMarkdown, selectedText) {
  if (!fullMarkdown || !selectedText) return selectedText;

  // If the selected text already contains markdown tokens, keep it
  if (hasMarkdownFormatting(selectedText)) {
    return selectedText;
  }

  // Helper: strip most markdown/whitespace for fuzzy matching
  const toPlain = (s) => s
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^\s*[#>\-•\d+.]+\s*/gm, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

  const plainSelected = toPlain(selectedText);
  const lines = fullMarkdown.split('\n');

  // Sliding window over lines to find a minimal block whose plain text contains the selection
  let best = null; // { start, end }
  for (let start = 0; start < lines.length; start++) {
    let acc = '';
    for (let end = start; end < lines.length && end - start < 30; end++) {
      acc += (end > start ? '\n' : '') + lines[end];
      if (toPlain(acc).includes(plainSelected)) {
        if (!best || (end - start) < (best.end - best.start)) {
          best = { start, end };
        }
        break;
      }
    }
  }

  if (best) {
    return lines.slice(best.start, best.end + 1).join('\n');
  }

  // Fallback: return original selected text
  return selectedText;
}

// Convert a DocumentFragment (from selection) back into lightweight markdown
function convertHtmlFragmentToMarkdown(fragment) {
  const parts = [];

  function walk(node) {
    if (!node) return;
    if (node.nodeType === Node.TEXT_NODE) {
      parts.push(node.nodeValue);
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    const el = node;
    const tag = el.tagName;

    // Block-level elements -> add newlines around content
    const isBlock = ['P', 'DIV', 'SECTION', 'ARTICLE', 'LI', 'UL', 'OL', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BR'].includes(tag);

    const pushChildren = () => {
      for (let i = 0; i < el.childNodes.length; i++) {
        walk(el.childNodes[i]);
      }
    };

    switch (tag) {
      case 'STRONG':
      case 'B':
        parts.push('**');
        pushChildren();
        parts.push('**');
        break;
      case 'EM':
      case 'I':
        parts.push('*');
        pushChildren();
        parts.push('*');
        break;
      case 'CODE':
        parts.push('`');
        pushChildren();
        parts.push('`');
        break;
      case 'A':
        {
          const href = el.getAttribute('href') || '';
          const textPartsStart = parts.length;
          pushChildren();
          const linkText = parts.splice(textPartsStart).join('');
          parts.push(`[${linkText}](${href})`);
        }
        break;
      case 'LI':
        parts.push('- ');
        pushChildren();
        parts.push('\n');
        break;
      case 'BR':
        parts.push('\n');
        break;
      default:
        if (isBlock) parts.push('\n');
        pushChildren();
        if (isBlock) parts.push('\n');
        break;
    }
  }

  for (let i = 0; i < fragment.childNodes.length; i++) {
    walk(fragment.childNodes[i]);
  }

  // Normalize excess whitespace/newlines
  return parts.join('')
    .replace(/\s+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function useTextSelection() {
  const [selectedText, setSelectedText] = useState('');
  const [selectedMarkdown, setSelectedMarkdown] = useState('');
  const [hasMarkdown, setHasMarkdown] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionRect, setSelectionRect] = useState(null);
  const [contextMenuVisible, setContextMenuVisible] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });

  const selectionTimeoutRef = useRef(null);
  const contextMenuRef = useRef(null);
  const lastSelectionRef = useRef('');
  const isActivelySelectingRef = useRef(false);
  const mouseDownTimeRef = useRef(0);
  
  // Clear all selection state
  const clearSelection = useCallback(() => {
    setSelectedText('');
    setSelectedMarkdown('');
    setHasMarkdown(false);
    setIsSelecting(false);
    setSelectionRect(null);
    setContextMenuVisible(false);
    lastSelectionRef.current = '';
    
    if (selectionTimeoutRef.current) {
      clearTimeout(selectionTimeoutRef.current);
    }
  }, []);
  
  // Handle text selection
  const handleSelectionChange = useCallback(() => {
    const selection = window.getSelection();

    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
      // Don't clear during active selection to prevent interference
      if (isActivelySelectingRef.current) {
        return;
      }

      // Only clear if we had a selection before (don't clear during active selection)
      if (selectionTimeoutRef.current) {
        clearTimeout(selectionTimeoutRef.current);
      }
      selectionTimeoutRef.current = setTimeout(() => {
        // Double-check that selection is still collapsed and not actively selecting
        const currentSelection = window.getSelection();
        if (!isActivelySelectingRef.current && (!currentSelection || currentSelection.rangeCount === 0 || currentSelection.isCollapsed)) {
          clearSelection();
        }
      }, 150);
      return;
    }
    
    const text = selection.toString().trim();
    
    // Check if selection is within interactive elements (inputs, buttons, etc.)
    const range = selection.getRangeAt(0);
    const container = range.commonAncestorContainer;
    const parentElement = container.nodeType === Node.TEXT_NODE 
      ? container.parentElement 
      : container;
    
    // Skip if selection is in form elements or buttons
    if (parentElement && (
      parentElement.tagName === 'INPUT' ||
      parentElement.tagName === 'TEXTAREA' ||
      parentElement.tagName === 'BUTTON' ||
      parentElement.contentEditable === 'true' ||
      parentElement.closest('button, input, textarea, [contenteditable="true"]')
    )) {
      return;
    }
    
    // Ignore very short selections or very long selections (likely accidental)
    if (text.length < 3 || text.length > 5000) {
      return;
    }

    // If actively selecting, defer heavy processing to avoid interference
    if (isActivelySelectingRef.current) {
      // Just update the text for now, defer full processing
      lastSelectionRef.current = text;
      return;
    }

    // Extract markdown and position data immediately while selection is still valid
    const markdown = extractMarkdownFromSelection(selection);
    const hasMarkdownFormat = hasMarkdownFormatting(markdown) || (markdown && markdown.includes('\n'));
    const rect = range.getBoundingClientRect();
    
    // Only proceed if we have a valid rectangle
    if (rect.width === 0 || rect.height === 0) {
      return;
    }
    
    const selectionData = {
      text,
      markdown,
      hasMarkdown: hasMarkdownFormat,
      rect: {
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        height: rect.height,
        bottom: rect.bottom + window.scrollY,
        right: rect.right + window.scrollX,
      }
    };
    
    // Update immediately for responsiveness, but debounce the UI updates
    if (selectionTimeoutRef.current) {
      clearTimeout(selectionTimeoutRef.current);
    }
    
    // Only update if the selection has meaningfully changed
    if (Math.abs(text.length - lastSelectionRef.current.length) > 5 || 
        text !== lastSelectionRef.current) {
      
      lastSelectionRef.current = text;
      
      selectionTimeoutRef.current = setTimeout(() => {
        // Use the captured selection data instead of re-querying selection
        setSelectedText(selectionData.text);
        setSelectedMarkdown(selectionData.markdown);
        setHasMarkdown(selectionData.hasMarkdown);
        setSelectionRect(selectionData.rect);
        setIsSelecting(true);
        setContextMenuVisible(false); // Close context menu when new selection is made
      }, 200); // Reduced timeout for better responsiveness when not actively selecting
    }
  }, [clearSelection]);
  
  // Handle right-click context menu
  const handleContextMenu = useCallback((event) => {
    const selection = window.getSelection();
    
    if (selection && selection.rangeCount > 0 && !selection.isCollapsed) {
      const text = selection.toString().trim();
      
      if (text.length >= 3 && text.length <= 5000) {
        event.preventDefault();
        
        // Clear any existing timeout to prevent conflicts
        if (selectionTimeoutRef.current) {
          clearTimeout(selectionTimeoutRef.current);
        }
        
        setContextMenuVisible(true);
        setContextMenuPosition({
          x: event.clientX,
          y: event.clientY,
        });
        
        // Update selection data immediately (no timeout for context menu)
        setSelectedText(text);
        const markdown = extractMarkdownFromSelection(selection);
        const hasMarkdownFormat = hasMarkdownFormatting(markdown) || (markdown && markdown.includes('\n'));
        setSelectedMarkdown(markdown);
        setHasMarkdown(hasMarkdownFormat);
      }
    }
  }, []);
  
  // Handle mouse events to track active selection
  const handleMouseDown = useCallback((event) => {
    // Only track on left mouse button
    if (event.button === 0) {
      isActivelySelectingRef.current = true;
      mouseDownTimeRef.current = Date.now();
    }
  }, []);

  const handleMouseUp = useCallback(() => {
    // Add small delay to ensure selection has stabilized
    setTimeout(() => {
      isActivelySelectingRef.current = false;
    }, 100);
  }, []);

  // Handle clicks outside context menu and general clicks
  const handleClickOutside = useCallback((event) => {
    if (contextMenuRef.current && !contextMenuRef.current.contains(event.target)) {
      setContextMenuVisible(false);
    }

    // Skip clearing selection if user is actively selecting or just finished selecting
    if (isActivelySelectingRef.current || (Date.now() - mouseDownTimeRef.current) < 200) {
      return;
    }

    // Clear selection when clicking outside of text content (with a small delay to allow for new selections)
    setTimeout(() => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed) {
        clearSelection();
      }
    }, 50);
  }, [clearSelection]);
  
  // Hide selection UI
  const hideSelection = useCallback(() => {
    clearSelection();
    
    // Clear the actual browser selection
    const selection = window.getSelection();
    if (selection) {
      selection.removeAllRanges();
    }
  }, [clearSelection]);
  
  // Set up event listeners
  useEffect(() => {
    // Add error handling for event listeners
    const safeHandleSelectionChange = (event) => {
      try {
        handleSelectionChange(event);
      } catch (error) {
        console.warn('Text selection handler error:', error);
      }
    };
    
    const safeHandleContextMenu = (event) => {
      try {
        handleContextMenu(event);
      } catch (error) {
        console.warn('Context menu handler error:', error);
      }
    };
    
    const safeHandleClickOutside = (event) => {
      try {
        handleClickOutside(event);
      } catch (error) {
        console.warn('Click outside handler error:', error);
      }
    };
    
    document.addEventListener('selectionchange', safeHandleSelectionChange);
    document.addEventListener('contextmenu', safeHandleContextMenu);
    document.addEventListener('click', safeHandleClickOutside);
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      document.removeEventListener('selectionchange', safeHandleSelectionChange);
      document.removeEventListener('contextmenu', safeHandleContextMenu);
      document.removeEventListener('click', safeHandleClickOutside);
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
      
      if (selectionTimeoutRef.current) {
        clearTimeout(selectionTimeoutRef.current);
      }
    };
  }, [handleSelectionChange, handleContextMenu, handleClickOutside, handleMouseDown, handleMouseUp]);
  
  return {
    selectedText,
    selectedMarkdown,
    hasMarkdown,
    isSelecting,
    selectionRect,
    contextMenuVisible,
    contextMenuPosition,
    contextMenuRef,
    hideSelection,
  };
}